import DestinationFacebook from '../../images/stream-destinations/destination-facebook.png';
import DestinationYoutube from '../../images/stream-destinations/destination-youtube.png';
import DestinationVimeo from '../../images/stream-destinations/destination-vimeo.png';
import DestinationLinkedIn from '../../images/stream-destinations/destination-linkedin.png';
import DestinationOther from '../../images/stream-destinations/destination-other.png';
import DestinationBrandlive from '../../images/stream-destinations/destination-brandlive.svg';
import DestinationMicrosoft from '../../images/stream-destinations/destination-microsoft.png';
import DestinationZoom from '../../images/stream-destinations/destination-zoom.png';
import DestinationStudio from '../../images/stream-destinations/destination-studio.png';

export interface SelectGroupOption {
	iconSize?: number;
	label: string;
	shortLabel?: string;
	value: any;
	selected?: boolean;
	description?: string;
	className?: string;
	icon?: string;
	iconColor?: string;
}

export enum BroadcastAudioCodec {
	DEFAULT_AC = 0,
	OPUS = 1,
	AAC = 2,
}

export enum BroadcastVideoCodec {
	DEFAULT_VC = 0,
	H264_BASELINE = 1,
	H264_MAIN = 2,
	H264_HIGH = 3,
	VP8 = 4,
}

export const BROADCAST_STATES = {
	OFF: 0, 				// server has not been created for this meeting - either too early or too late
	STARTING_UP: 1, 		// server is starting up but is not yet available
	READY: 2, 				// server can be broadcast to
	BROADCAST_STARTED: 3, 	// start response received from broadcast server - it's actually live
	BROADCAST_STOPPED: 4, 	// stopped signal has been received from server
	DRAINING: 5, 			// server is actively terminating - may go unused
	KILLED: 6, 				// server is dead and unusable
	BROADCAST_STARTING: 7, 	// start signal sent to server but hasn't confirmed that it's live
	BROADCAST_FAILED: 8, 	// broadcast signal sent but the broadcast failed
	BROADCAST_PREWARMING: 9,	// server is starting a fake broadcast to sync. Random necessary step.
	BROADCAST_PREWARM_FAILED: 10,
};

export enum BroadcastStatus {
	ready = 'ready',
	canceling = 'canceling',
	starting = 'starting',
	active = 'active',
	ending = 'ending',
	complete = 'complete',
	failed = 'failed',
	aborted = 'aborted',
	limitReached = 'limitReached',
}

export const DESTINATION_TYPE = {
	BRANDLIVE: -1,
	STREAMS: 0,
	FACEBOOK: 2,
	YOUTUBE: 3,
	VIMEO: 4,
	LINKEDIN: 5,
	OTHER: 6,
	MICROSOFT: 7,
	ZOOM: 8,
	STUDIO: 10,
};

export const RESOLUTION_OPTIONS: SelectGroupOption[] = [
	{ label: '1080p', value: '1080p' },
	{ label: '720p', value: '720p' },
	{ label: '480p', value: '480p' },
];

export const CUSTOM_DESTINATION_OPTIONS: SelectGroupOption[] = [
	{ label: 'Brandlive', value: DESTINATION_TYPE.STREAMS, description: DestinationBrandlive },
	{ label: 'Studio', value: DESTINATION_TYPE.STUDIO, description: DestinationStudio },
	{ label: 'Facebook', value: DESTINATION_TYPE.FACEBOOK, description: DestinationFacebook },
	{ label: 'YouTube', value: DESTINATION_TYPE.YOUTUBE, description: DestinationYoutube },
	{ label: 'Vimeo', value: DESTINATION_TYPE.VIMEO, description: DestinationVimeo },
	{ label: 'LinkedIn', value: DESTINATION_TYPE.LINKEDIN, description: DestinationLinkedIn },
	{ label: 'Microsoft Teams', value: DESTINATION_TYPE.MICROSOFT, description: DestinationMicrosoft },
	{ label: 'Zoom Webinar/Events', value: DESTINATION_TYPE.ZOOM, description: DestinationZoom },
	{ label: 'Other', value: DESTINATION_TYPE.OTHER, description: DestinationOther },
];

export enum BroadcastWarningTypes {
	recordingStartFailure = 'recordingStartFailure',
}

export const ACTIVE_STATUSES = [
	BroadcastStatus.starting,
	BroadcastStatus.active,
];

export const ENDING_STATUSES = [
	BroadcastStatus.ending,
	BroadcastStatus.canceling,
];

export const IN_PROGRESS_STATUSES = [
	...ACTIVE_STATUSES,
	...ENDING_STATUSES,
];

export enum StreamStatus {
	active = 'active',
	finished = 'finished',
	failed = 'failed',
}

export enum ProviderTypes {
	liveKit = 'liveKit',
}

export interface StreamInfo {
	url: string;
	startTime: number;
	endTime: number;
	status: StreamStatus;
	error: string;
}

export interface Broadcast {
	id?: string;
	provider?: ProviderTypes;
	status: BroadcastStatus;
	startTime?: number;
	endTime?: number;
	outputs?: StreamInfo[];
	encodingOptions?: { [key: string]: any };
}

export interface BroadcastEncodingOptions {
	width?: number;
	height?: number;
	depth?: number;
	framerate?: number;
	audioCodec?: BroadcastAudioCodec;
	audioBitrate?: number;
	audioFrequency?: number;
	videoCodec?: BroadcastVideoCodec;
	videoBitrate?: number;
	keyFrameInterval?: number;
}

export interface Destination {
	id?: number;
	name?: string;
	url: string;
	streamKey: string;
	logo?: string;
	type?: number;
	selected: boolean;
	isBlp?: boolean;
	createdAt?: Date;
	updatedAt?: Date;
}

export interface DestinationStatus {
	id: number;
	name: string;
	type: number;
	logo: string;
	status: StreamStatus;
	error: string;
}

/*
	when we open the modal, check if anyone is currently editing this
	shot, if they are, update our app with edits
*/
export interface BroadcastStats {
	bitrate: number;		// in kbps
	fps: number;			// in frames per second
	health: number;			// - 1 is good
	// - less than 1 the server is falling behind
	// - less than 0.95 frames will be lost, broadcast will buffer in video player periodically
}

export const STREAM_STATES = {
	OFF: 0,
	PRE: 1,
	MAIN: 2,
	POST: 3,
};

export interface BroadcastingState {
	server: any;
	loadingBroadcastServer: boolean;
	loadingBroadcastServerError: boolean;
	broadcasting: boolean;
	startingBroadcast: boolean;
	failedToStartBroadcast: boolean;
	stoppingBroadcast: boolean;
	failedToStopBroadcast: boolean;
	serverState: number;
	streamState: number;
	previousServerState: number;
	stats: BroadcastStats;
	goLiveButtonClicked: boolean;
	selectedRecordingOption: number;
	broadcastStartingShotId: number;
	broadcastStartedBy: number;
	currentRecordingStreamId: string;
	broadcastWarnings: BroadcastWarningTypes[],
	isoArchiveId?: string;
	goLiveFromModal: boolean;
	addingDestination: Destination | null;
	editingDestination: string;
	savingDestination: boolean;
	spielbergBroadcast: Broadcast | null;
	destinations: Destination[];
	resolution: string;
	destinationStatus: DestinationStatus[];
}
